import { HttpParams } from '@angular/common/http';
import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import {
    MAT_DIALOG_DATA,
    MatDialogRef,
} from '@angular/material/dialog';
import { FormGroup, UntypedFormBuilder } from '@angular/forms';
import { forkJoin, of, Subject } from 'rxjs';
import { catchError, finalize, takeUntil } from 'rxjs/operators';

import { AppNotificationService } from '@core/services/app-notification.service';
import { TranslationService } from '@core/translation/translation.service';
import { OrdersApi } from '@bcmServices/index';
import { EmployeesFacade } from '@modules/bcm/@core/state-management/employees/employees.facade';
import { DepartmentsFacade } from '@modules/bcm/@core/state-management/departments/departments.facade';
import { BcmTenantService } from '@modules/bcm/bcm-tenant.service';
import { BcmTenantPermission } from '@modules/bcm/bcm-tenant-permission';
import { BcmDepartment } from '@shared/models/bcm-department';
import { BcmEmployee } from '@shared/models/bcm-employee';
import { BcmOrder } from '@shared/models/bcm-order';
import { isOrderStatusLockedForEditing } from '@shared/helpers/order-status-model.util';
import {
    applyOrderAssigneeFieldsToOrder,
    isOrderAssigneeFormValueComplete,
    mapOrderAssigneeFormValueToPayload,
    resolveOrderAssigneeFormDefaults,
} from '@shared/helpers/order-order-assignee.util';
import { OrderTaskPositionAssigneeType } from '@shared/helpers/order-task-position-assignee.util';
import { BcmCapabilityService } from '@modules/bcm/capabilities/bcm-capability.service';
import { BcmUserPermission } from '@modules/bcm/bcm-user-permission';

export interface AssignUserDialogData {
    order: BcmOrder;
}

@Component({
    selector: 'assign-user',
    templateUrl: './assign-user.component.html',
    styleUrls: ['./assign-user.component.scss'],
    standalone: false,
})
export class AssignUserComponent implements OnInit, OnDestroy {

    private readonly destroy$ = new Subject<void>();

    isSaving = false;
    isLookupLoading = true;
    assigneeLookupLimited = false;
    form: FormGroup;
    employees: BcmEmployee[] = [];
    departments: BcmDepartment[] = [];

    constructor(
        private readonly _dialogRef: MatDialogRef<AssignUserComponent>,
        private readonly _formBuilder: UntypedFormBuilder,
        private readonly _appNotificationService: AppNotificationService,
        private readonly _ordersApi: OrdersApi,
        private readonly _employeesFacade: EmployeesFacade,
        private readonly _departmentsFacade: DepartmentsFacade,
        private readonly _bcmTenantService: BcmTenantService,
        private readonly _bcmCapabilityService: BcmCapabilityService,
        @Inject(MAT_DIALOG_DATA) public data: AssignUserDialogData,
    ) {
        this.form = this._formBuilder.group({
            assigneeType: [null],
            bcm_employees_id: [null],
            bcm_departments_id: [null],
        });
    }

    get hasDepartmentsModule(): boolean {
        return this._bcmTenantService.checkPermission(BcmTenantPermission.DEPARTMENTS);
    }

    ngOnInit(): void {
        const canLoadEmployees = this._bcmCapabilityService.shouldPrefetch('orders.taskAssigneeLookups');
        const canLoadDepartments = this.hasDepartmentsModule
            && this._bcmCapabilityService.hasAnyPermission(BcmUserPermission.DEPARTMENTS_READ);
        this.assigneeLookupLimited = !canLoadEmployees || (this.hasDepartmentsModule && !canLoadDepartments);

        const employees$ = canLoadEmployees
            ? this._employeesFacade.loadPage(
                new HttpParams().set('size', 1000).set('sort', 'employee_number'),
            ).pipe(catchError(() => of(null)))
            : of(null);

        const departments$ = canLoadDepartments
            ? this._departmentsFacade.loadPage(new HttpParams().set('size', 1000)).pipe(catchError(() => of(null)))
            : of(null);

        forkJoin({ employees: employees$, departments: departments$ })
            .pipe(
                takeUntil(this.destroy$),
                finalize(() => { this.isLookupLoading = false; }),
            )
            .subscribe(({ employees, departments }) => {
                this.employees = employees?.results ?? [];
                this.departments = departments?.results ?? [];

                const defaults = resolveOrderAssigneeFormDefaults(this.data.order, this.employees);

                if (!this.hasDepartmentsModule && !defaults.assigneeType) {
                    defaults.assigneeType = OrderTaskPositionAssigneeType.Employee;
                }

                this.form.patchValue(defaults, { emitEvent: false });
            });
    }

    onSave(): void {
        if (this.form.invalid || this.isSaving || this.isLookupLoading) {
            return;
        }

        const order = this.data.order;

        if (isOrderStatusLockedForEditing(order.lastStatus)) {
            this._appNotificationService.showError(
                TranslationService.translate('bcmErrors.orders.lockedForEditing'),
            );

            return;
        }

        const rawAssignee = this.form.getRawValue();

        if (!this.hasDepartmentsModule
            && rawAssignee.assigneeType !== OrderTaskPositionAssigneeType.Department) {
            rawAssignee.assigneeType = OrderTaskPositionAssigneeType.Employee;
        }

        if (!isOrderAssigneeFormValueComplete(rawAssignee)) {
            this.form.markAllAsTouched();
            this._appNotificationService.showError(
                TranslationService.translate('widgets.docksite.orderDetailV2.taskPositions.assigneeSelectionRequired'),
            );

            return;
        }

        const assigneePatch = mapOrderAssigneeFormValueToPayload(rawAssignee);

        const {
            todos: _todos,
            ...orderWithoutTodos
        } = order as BcmOrder & { todos?: unknown };

        const body = {
            ...orderWithoutTodos,
            id: order.id,
            ...assigneePatch,
        } as BcmOrder;

        this.isSaving = true;

        this._ordersApi.update(body, false)
            .pipe(finalize(() => { this.isSaving = false; }))
            .subscribe({
                next: (updatedOrder) => {
                    applyOrderAssigneeFieldsToOrder(order, updatedOrder);
                    this._dialogRef.close(updatedOrder);
                    this._appNotificationService.showSuccess(
                        TranslationService.translate('bcm.docksiteOrder.notify.dataSavedSuccessfully'),
                    );
                },
                error: () => {
                    this._appNotificationService.showError(
                        TranslationService.translate('common.error.saveFailed'),
                    );
                },
            });
    }

    onClickClose(): void {
        this._dialogRef.close();
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }
}
